<script lang="ts">
  import type { RetailerUser } from "@shared/types/User";
  import PermissionsInfoCard from "./PermissionsInfoCard.svelte";
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import { Http } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";
  import PhoneInput from "@shared/svelte/Components/PhoneInput.svelte";
  import * as _ from "lodash";
  import { goto, page } from "@roxi/routify";

  export let autoWidth;
  export let modal;
  export let users: RetailerUser[];
  let submittingProfile = false;

  let errors = { messages: [] };
  $: canSubmit = false;

  const runCanSubmitModal = function () {
    if (!modal.user) {
      return false;
    }
    if (!modal.isExistingUserCheckComplete && modal.user.email && modal.user.role) {
      return !submittingProfile;
    }
    if (
      modal.isExistingUserCheckComplete &&
      modal.needToCreateNewUser &&
      modal.user.email &&
      modal.user.first_name &&
      modal.user.last_name &&
      modal.user.role
    ) {
      return !submittingProfile;
    }
    return false;
  };
  $: {
    modal = modal;
    canSubmit = runCanSubmitModal();
  }

  const roles: { id: any; label: any }[] = window["C"].rolesNotInRetailerOrganizationMandate;

  let paymentsEnabled = window["$rootScope"].user.term_payments;

  function renderErrors(res) {
    submittingProfile = false;
    if (res.error && res.error_code) {
      errors = { messages: [res.error] };
    } else if (res.status && res.status >= 400 && res.status <= 499) {
      errors = res.errors;
    } else {
      alert("There was a server error when creating this employee.");
    }
  }

  function sendToApi(action, success_cb, error_cb) {
    submittingProfile = true;
    Http(
      {
        method: "POST",
        url: "/api/retailer/employees/" + action,
        data: { user: modal.user },
      },
      function (res) {
        submittingProfile = false;
        success_cb(res);
        modal = {};
      },
      function (res) {
        submittingProfile = false;
        error_cb(res);
      }
    );
  }

  function tryAddingNewUser() {
    sendToApi(
      "create_with_new_user",
      function (success_res) {
        Flash.now({ message: "We have created this employee successfully", type: "success" });
        $goto("/settings/employees/:userId", { userId: success_res.id });
      },
      function (error_res) {
        renderErrors(error_res);
      }
    );
  }

  function tryAddingWithExistingUser() {
    sendToApi(
      "create_with_existing_user",
      function (success_res) {
        Flash.now({ message: "User added as employee to this location.", type: "success" });
        users = [...users, success_res];
      },
      function (error_res) {
        // User doesn't exist, will need to fill out full form
        if (_.get(error_res, "error_code") == "e100") {
          modal.isExistingUserCheckComplete = true;
          modal.needToCreateNewUser = true;
          return;
        }
        renderErrors(error_res);
      }
    );
  }

  const addUser = function () {
    submittingProfile = true;
    if (!modal.isExistingUserCheckComplete) {
      tryAddingWithExistingUser();
    } else if (modal.needToCreateNewUser) {
      tryAddingNewUser();
    }
  };

</script>

<Modal bind:show={modal.open}>
  <div class="background-white add-user-modal" style="width: 95vw; max-width: 600px;">
    <header class="modal__header padding--small">
      <div class="media">
        <div class="default-subheader--bold media-fluid">
          {#if !modal.isExistingUserCheckComplete}
            <span>Add Employee Account</span>
          {/if}
          {#if modal.isExistingUserCheckComplete && modal.needToCreateNewUser}
            <span>Create New Employee Account</span>
          {/if}
        </div>
        <Icon
          type="X"
          height="16"
          width="16"
          className="media-fixed cursor-pointer align-self-center align-right close-modal"
          onPress={() => (modal.open = false)}
        />
      </div>
    </header>

    <FormErrors {errors} />

    <div class="modal__scroll-container padding--small">
      <div class="grid-container">
        <div class="grid-row margin-top--small">
          <div class="grid-col c12-12">
            <div class="label input__label--left padding-bottom--smallest">Email Address</div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                bind:value={modal.user.email}
                disabled={submittingProfile}
                name="email"
              />
            </div>
          </div>
        </div>

        <div class="grid-row margin-bottom--medium">
          <div class="grid-col c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Role
            </div>
            <div class="input-wrapper select-style">
              <select id="role_select" bind:value={modal.user.role}>
                {#each roles as role}
                  <option value={role.id}>{role.label}</option>
                {/each}
              </select>
            </div>
          </div>
        </div>
        <PermissionsInfoCard {autoWidth} {paymentsEnabled} />

        {#if modal.isExistingUserCheckComplete && modal.needToCreateNewUser}
          <div class="padding-top--small">
            <div class="grid-row">
              <div class="grid-col c12-12">
                <div class="default-subheader--regular align-center">
                  This email address is not yet registered with Provi - please provide the following
                  details to create an account.
                </div>
              </div>
            </div>
            <div class="grid-row">
              <div class="grid-col c12-12">
                <div class="label input__label--left padding-bottom--smallest margin-top--small">
                  Cell Phone
                </div>
                <div class="input-wrapper full-width">
                  <PhoneInput
                    bind:value={modal.user.phone}
                    placeholder="Phone"
                    disabled={submittingProfile}
                    id="phone"
                    extraClasses="phone-input input"
                  />
                </div>
              </div>
            </div>

            <div class="grid-row padding-bottom--small--md">
              <div class="grid-col c12-12 c6-12--md">
                <div class="label input__label--left padding-bottom--smallest margin-top--small">
                  First Name
                </div>
                <div class="input-wrapper full-width">
                  <input
                    type="text"
                    class="input"
                    bind:value={modal.user.first_name}
                    disabled={submittingProfile}
                    name="first_name"
                  />
                </div>
              </div>
              <div class="grid-col c12-12 c6-12--md">
                <div class="label input__label--left padding-bottom--smallest margin-top--small">
                  Last Name
                </div>
                <div class="input-wrapper full-width">
                  <input
                    type="text"
                    class="input"
                    bind:value={modal.user.last_name}
                    disabled={submittingProfile}
                    name="last_name"
                  />
                </div>
              </div>
            </div>
          </div>
        {/if}
      </div>
    </div>
    <footer class="modal__footer align-right padding--small">
      <button class="button button--primary" disabled={!canSubmit} on:click={addUser}> Add </button>
    </footer>
  </div>
</Modal>
